import React from "react";
import { motion } from "framer-motion";
import { FaUsers, FaCogs, FaChartLine, FaUserCog, FaHandshake, FaAward, FaLightbulb, FaClock } from "react-icons/fa";
import FeatureCard from "../components/FeatureCard";
import PageBanner from "../components/Pagebanner";
import { Link } from "react-router-dom";

const features = [
	{
		icon: <FaUsers size={40} className="text-primary" />,
		title: "Quality Assurance",
		underline: true,
		description:
			"We prioritize quality, ensuring every product meets the highest industry standards.",
	},
	{
		icon: <FaCogs size={40} className="text-primary" />,
		title: "Consistency",
		underline: true,
		description:
			"From materials to fabrication, we provide a wide selection and comprehensive solutions.",
	},
	{
		icon: <FaChartLine size={40} className="text-primary" />,
		title: "Innovation",
		underline: true,
		description:
			"We're dedicated to customer satisfaction and staying ahead with cutting-edge technology.",
	},
	{
		icon: <FaUserCog size={40} className="text-primary" />,
		title: "Timely Delivery",
		underline: true,
		description: "We ensure punctual delivery to keep your project on track, every time.",
	},
];

const whyChooseUs = [
	{
		icon: <FaAward size={32} className="text-yellow-400" />,
		title: "Certified Excellence",
		desc: "Internationally recognized certifications and awards.",
	},
	{
		icon: <FaHandshake size={32} className="text-secondary" />,
		title: "Trusted Partnerships",
		desc: "Long-term relationships with leading brands and clients.",
	},
	{
		icon: <FaLightbulb size={32} className="text-primary" />,
		title: "Innovative Solutions",
		desc: "We embrace new technologies and creative problem-solving.",
	},
	{
		icon: <FaClock size={32} className="text-blue-400" />,
		title: "24/7 Support",
		desc: "Our team is always available for your needs.",
	},
];

const journey = [
	{
		year: "2005",
		title: "Founded",
		desc: "Al-Khobar Trading was established with a vision to deliver excellence in industrial solutions.",
	},
	{
		year: "2010",
		title: "Expansion",
		desc: "Expanded our services to include engineering, maintenance, and construction.",
	},
	{
		year: "2017",
		title: "Innovation",
		desc: "Adopted cutting-edge technology and sustainable practices.",
	},
	{
		year: "2024",
		title: "Today",
		desc: "A trusted partner for hundreds of clients across Saudi Arabia and beyond.",
	},
];

const About = () => {
	return (
		<div className="relative text-gray-800 bg-gradient-to-b from-white via-yellow-50 to-yellow-100 pb-20 overflow-hidden">
			{/* Animated Blobs */}
			<div className="absolute -top-32 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl z-0" />
			<div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary/10 rounded-full blur-2xl z-0" />

			{/* Banner */}
			<PageBanner
				title="About Us"
				breadcrumb="Home / About"
				backgroundImage="/images/industry.jpg"
			/>

			{/* Tagline & Intro */}
			<div className="max-w-3xl mx-auto text-center mt-12 mb-10 px-4 relative z-10">
				<motion.h2
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					className="text-4xl md:text-5xl font-extrabold text-primary mb-4"
				>
					Empowering Industry. Enabling Progress.
				</motion.h2>
				<motion.p
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.2 }}
					className="text-lg md:text-xl text-gray-700"
				>
					Al-Khobar Trading is a Saudi-based company providing comprehensive services in construction, maintenance, engineering, and more. Our mission is to offer reliable and efficient solutions to our clients with a focus on quality, safety, and excellence.
				</motion.p>
			</div>

			{/* About Section */}
			<div className="mt-10 grid md:grid-cols-2 gap-10 items-center px-6 md:px-16 relative z-10">
				<motion.img
					initial={{ opacity: 0, x: -40 }}
					whileInView={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.6 }}
					src="/images/trustus.jpg"
					alt="About Al-Khobar Trading"
					className="rounded-2xl w-full shadow-2xl p-2 bg-white/80 backdrop-blur-lg"
				/>

				<motion.div
					initial={{ opacity: 0, x: 40 }}
					whileInView={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.6 }}
				>
					<h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
						Welcome to Al-Khobar Trading
					</h2>
					<p className="text-lg mb-4 leading-relaxed">
						We are dedicated to exceeding client expectations through our skilled workforce, innovative technology, and ethical practices. With years of experience, we strive to remain leaders in our industry.
					</p>
					<div className="mt-6">
						<Link
							to="/contact"
							className="inline-block bg-primary text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-secondary transition"
						>
							Contact Us
						</Link>
					</div>
				</motion.div>
			</div>

			{/* Our Journey Timeline */}
			{/* <div className="max-w-5xl mx-auto mt-20 mb-16 px-4 relative z-10 px-10">
				<motion.h3
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					className="text-2xl md:text-3xl font-bold text-secondary mb-8 text-center"
				>
					Our Journey
				</motion.h3>
				<div className="relative border-l-4 border-primary/20 pl-8">
					{journey.map((item, idx) => (
						<motion.div
							key={item.year}
							initial={{ opacity: 0, x: -30 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5, delay: idx * 0.1 }}
							className="mb-10"
						>
							<div className="flex items-center gap-4">
								<div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-sm shadow-lg border-4 border-white -ml-14">
									{item.year}
								</div>
								<div>
									<div className="text-lg font-bold text-primary">
										{item.title}
									</div>
									<div className="text-gray-700">{item.desc}</div>
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</div> */}
			{/* Mission Statement */}
			<div className="mt-20 mb-10 text-center px-6 md:px-0 relative z-10">
				<motion.h3
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					className="text-2xl md:text-3xl font-bold text-secondary mb-4"
				>
					Our Mission
				</motion.h3>
				<motion.p
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.2 }}
					className="text-lg md:text-xl max-w-3xl mx-auto text-gray-700"
				>
					To deliver world-class solutions with integrity, innovation, and a commitment to excellence—empowering our clients to achieve their goals and build a better future.
				</motion.p>
			</div>

			{/* Why Choose Us */}
			<div className="max-w-6xl mx-auto mt-20 mb-16 px-4 relative z-10">
				<motion.h3
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					className="text-2xl md:text-3xl font-bold text-primary mb-8 text-center"
				>
					Why Choose Us
				</motion.h3>
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
					{whyChooseUs.map((item, idx) => (
						<motion.div
							key={item.title}
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: idx * 0.1 }}
							className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-6 flex flex-col items-center text-center border border-primary/10 hover:scale-105 transition-transform"
						>
							<div className="mb-4">{item.icon}</div>
							<div className="font-bold text-lg text-primary mb-2">
								{item.title}
							</div>
							<div className="text-gray-700">{item.desc}</div>
						</motion.div>
					))}
				</div>
			</div>

			{/* Feature Cards */}
			{/* <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16 px-6 md:px-16 relative z-10">
				{features.map((feature, index) => (
					<motion.div
						key={index}
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.1 }}
					>
						<FeatureCard
							icon={feature.icon}
							title={feature.title}
							description={feature.description}
							className="hover:scale-105 hover:shadow-2xl transition-transform duration-300"
						/>
					</motion.div>
				))}
			</div> */}

			{/* Call to Action */}
			<div className="max-w-3xl mx-auto mt-20 mb-10 px-4 text-center relative z-10">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					className="bg-primary/90 text-white rounded-2xl shadow-xl p-8 flex flex-col items-center"
				>
					<h3 className="text-2xl md:text-3xl font-bold mb-4">
						Ready to work with us?
					</h3>
					<p className="mb-6 text-lg">
						Contact our expert team today for a consultation and discover how our customized solutions can exceed your expectations.
					</p>
					<Link
						to="/contact"
						className="inline-block bg-white text-primary px-8 py-3 rounded-full font-bold shadow hover:bg-secondary hover:text-white transition"
					>
						Get in Touch
					</Link>
				</motion.div>
			</div>
		</div>
	);
};

export default About;
